import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { QuickAmounts } from "@/components/QuickAmounts";
import { storage } from "@/lib/storage";
import { getCurrency, formatMoney } from "@/lib/currencies";
import { Check, Keyboard } from "lucide-react";

export default function ReceiveManual() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [note, setNote] = useState("");
  const [currency, setCurrency] = useState(storage.getCurrency());
  const [done, setDone] = useState(false);

  const cur = getCurrency(currency);
  const valid = Number(amount) > 0;

  const submit = () => {
    storage.addTransaction({
      direction: "received",
      amount: Number(amount),
      currency,
      method: "manual",
      note,
      customer: customerName,
    });
    setDone(true);
    setTimeout(() => navigate("/merchant"), 1200);
  };

  return (
    <div className="page-shell" data-testid="receive-manual-page">
      <Header title="Manual Entry" subtitle="Enter amount received" />

      <div className="card-glass gold-glow rounded-2xl p-6 fade-slide-up">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Keyboard size={16} className="text-[#E6B455]" />
            <span className="stat-label">Amount received</span>
          </div>
          <CurrencyPicker value={currency} onChange={setCurrency} compact />
        </div>
        <div className="flex items-center gap-3 mb-6">
          <span className="text-3xl font-bold text-[#E6B455]">{cur.symbol}</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-5xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/20 min-w-0"
            data-testid="manual-amount-input"
            autoFocus
          />
        </div>

        <QuickAmounts currency={currency} onPick={setAmount} selected={amount} />

        <div className="space-y-3">
          <input
            type="text"
            placeholder="Customer name (optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="w-full bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35"
            data-testid="manual-customer-input"
          />
          <input
            type="text"
            placeholder="Note / Reference (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35"
            data-testid="manual-note-input"
          />
        </div>
      </div>

      {valid && (
        <div className="mt-4 text-center fade-slide-up">
          <div className="text-[11px] uppercase tracking-wider text-[#F7E6B4]/55">You will receive</div>
          <div className="text-2xl font-bold gold-text mt-1" data-testid="manual-preview">
            {formatMoney(amount, currency)}
          </div>
        </div>
      )}

      <div className="mt-6">
        {done ? (
          <div className="btn-gold rounded-xl p-4 flex items-center justify-center gap-2" data-testid="manual-done">
            <Check size={18} /> Transaction Saved!
          </div>
        ) : (
          <button
            onClick={submit}
            disabled={!valid}
            className="btn-gold w-full py-3.5 rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="manual-submit-btn"
          >
            Record Payment
          </button>
        )}
      </div>
    </div>
  );
}
